// Список-вид пространства: плоская таблица всех карточек со всех досок.
// Альтернатива канвасному виду — для случаев когда хочется "посмотреть всё разом".
// Загружает full-данные каждой доски параллельно через api.boards.getFull;
// карточки кешируются TanStack Query, поэтому при повторном открытии — мгновенно.

import { useEffect, useMemo, useState } from 'react';
import { CalendarDays, AlertCircle } from 'lucide-react';
import clsx from 'clsx';
import { api } from '../lib/api';
import { TaskModal } from './TaskModal';
import type { BoardRow, CardRow, ColumnRow, LaneRow, Priority } from '../types';

const PRIORITY_ORDER: Record<Priority, number> = { critical: 0, high: 1, med: 2, low: 3 };
const PRIORITY_COLORS: Record<Priority, string> = {
  low: '#9ca3af',
  med: '#3b82f6',
  high: '#f59e0b',
  critical: '#ef4444',
};

type SortKey = 'priority' | 'due' | 'created' | 'board';

interface SpaceListViewProps {
  boards: BoardRow[];
}

export function SpaceListView({ boards }: SpaceListViewProps) {
  const [sortKey, setSortKey] = useState<SortKey>('priority');
  const [boardFilter, setBoardFilter] = useState<Set<string>>(new Set());
  const [priorityFilter, setPriorityFilter] = useState<Set<Priority>>(new Set());
  const [openCardId, setOpenCardId] = useState<string | null>(null);

  // useQuery вызывается в цикле — это безопасно, пока массив `boards` стабилен
  // в пределах рендера. Список досок редко меняется внутри space, и React
  // не пересортировывает hook order при добавлении/удалении доски (изменится
  // длина массива, но это эквивалентно условному рендеру дочерних компонентов:
  // sub-компонент SpaceListBoardLoader держит ровно один useQuery).
  // Чтобы избежать любых рисков с количеством hooks, выносим запрос в дочерний
  // компонент через ListBoardCards, см. ниже.

  const boardIds = useMemo(() => boards.map((b) => b.id), [boards]);

  return (
    <div className="flex-1 overflow-auto p-6">
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <span className="text-xs font-semibold uppercase tracking-wider text-ink-faint_l dark:text-ink-faint_d mr-1">
            Сортировка:
          </span>
          {(
            [
              ['priority', 'Приоритет'],
              ['due', 'Дедлайн'],
              ['created', 'Создание'],
              ['board', 'Доска'],
            ] as const
          ).map(([k, label]) => (
            <button
              key={k}
              type="button"
              onClick={() => setSortKey(k)}
              className={clsx(
                'px-2.5 py-1 text-xs rounded transition',
                sortKey === k
                  ? 'bg-accent text-white'
                  : 'bg-surface2-light dark:bg-surface2-dark text-ink-muted_l dark:text-ink-muted_d hover:bg-surface3-light dark:hover:bg-surface3-dark',
              )}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-4">
          <span className="text-xs font-semibold uppercase tracking-wider text-ink-faint_l dark:text-ink-faint_d mr-1">
            Приоритет:
          </span>
          {(['critical', 'high', 'med', 'low'] as Priority[]).map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => {
                setPriorityFilter((prev) => {
                  const next = new Set(prev);
                  if (next.has(p)) next.delete(p);
                  else next.add(p);
                  return next;
                });
              }}
              className={clsx(
                'inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded-full ring-1 transition',
                priorityFilter.has(p) || priorityFilter.size === 0
                  ? 'ring-line-light dark:ring-line-dark'
                  : 'opacity-40 ring-transparent',
              )}
              style={{
                color: PRIORITY_COLORS[p],
                background: `${PRIORITY_COLORS[p]}1f`,
              }}
            >
              {p}
            </button>
          ))}
          {boards.length > 1 && (
            <>
              <span className="text-xs font-semibold uppercase tracking-wider text-ink-faint_l dark:text-ink-faint_d ml-3 mr-1">
                Доска:
              </span>
              {boards.map((b) => (
                <button
                  key={b.id}
                  type="button"
                  onClick={() => {
                    setBoardFilter((prev) => {
                      const next = new Set(prev);
                      if (next.has(b.id)) next.delete(b.id);
                      else next.add(b.id);
                      return next;
                    });
                  }}
                  className={clsx(
                    'px-2 py-0.5 text-[11px] rounded-full ring-1 transition',
                    boardFilter.has(b.id) || boardFilter.size === 0
                      ? 'ring-line-light dark:ring-line-dark text-ink-light dark:text-ink-dark'
                      : 'opacity-40 ring-transparent text-ink-faint_l dark:text-ink-faint_d',
                  )}
                >
                  {b.title}
                </button>
              ))}
            </>
          )}
        </div>

        <ListAllCards
          boardIds={boardIds}
          sortKey={sortKey}
          boardFilter={boardFilter}
          priorityFilter={priorityFilter}
          onOpenCard={setOpenCardId}
        />

        {openCardId && (
          <TaskModal cardId={openCardId} onClose={() => setOpenCardId(null)} boardCards={[]} />
        )}
      </div>
    </div>
  );
}

interface ListAllCardsProps {
  boardIds: string[];
  sortKey: SortKey;
  boardFilter: Set<string>;
  priorityFilter: Set<Priority>;
  onOpenCard: (id: string) => void;
}

// Компонент-агрегатор: на каждый boardId — отдельный sub-компонент с useQuery.
// Так длина «массива hooks» в каждом sub-компоненте всегда одна — безопасно.
function ListAllCards({ boardIds, sortKey, boardFilter, priorityFilter, onOpenCard }: ListAllCardsProps) {
  const [bundles, setBundles] = useState<Record<string, BoardBundle>>({});

  const handleBundleReady = (boardId: string, bundle: BoardBundle | null) => {
    setBundles((prev) => {
      if (!bundle) {
        if (!(boardId in prev)) return prev;
        const next = { ...prev };
        delete next[boardId];
        return next;
      }
      const existing = prev[boardId];
      if (
        existing &&
        existing.cards.length === bundle.cards.length &&
        existing.board.title === bundle.board.title
      ) {
        // Поверхностная защита от лишних setState — список карточек
        // обновится через invalidate из других мест автоматически.
        return prev;
      }
      return { ...prev, [boardId]: bundle };
    });
  };

  const rows = useMemo(() => {
    const all: Row[] = [];
    for (const boardId of boardIds) {
      const bundle = bundles[boardId];
      if (!bundle) continue;
      if (boardFilter.size > 0 && !boardFilter.has(boardId)) continue;
      const colsById = new Map(bundle.columns.map((c) => [c.id, c]));
      const lanesById = new Map(bundle.lanes.map((l) => [l.id, l]));
      for (const card of bundle.cards) {
        if (card.archived) continue;
        if (priorityFilter.size > 0 && !priorityFilter.has(card.priority)) continue;
        all.push({
          card,
          boardTitle: bundle.board.title,
          columnTitle: colsById.get(card.columnId)?.title ?? '?',
          laneTitle: lanesById.get(card.laneId)?.title ?? '',
        });
      }
    }
    all.sort((a, b) => {
      switch (sortKey) {
        case 'priority':
          return PRIORITY_ORDER[a.card.priority] - PRIORITY_ORDER[b.card.priority];
        case 'due': {
          const ad = a.card.dueDate ? new Date(a.card.dueDate).getTime() : Infinity;
          const bd = b.card.dueDate ? new Date(b.card.dueDate).getTime() : Infinity;
          return ad - bd;
        }
        case 'created':
          // sortOrder ≈ хронология (lexorank float, новые карточки получают больший)
          return b.card.sortOrder - a.card.sortOrder;
        case 'board':
          return a.boardTitle.localeCompare(b.boardTitle, 'ru');
      }
    });
    return all;
  }, [bundles, boardIds, sortKey, boardFilter, priorityFilter]);

  const loadedCount = Object.keys(bundles).length;

  return (
    <>
      {boardIds.map((id) => (
        <BoardLoader key={id} boardId={id} onLoaded={handleBundleReady} />
      ))}
      {loadedCount === 0 ? (
        <div className="py-16 text-center text-ink-faint_l dark:text-ink-faint_d text-sm">
          Загрузка карточек со всех досок…
        </div>
      ) : rows.length === 0 ? (
        <div className="py-16 text-center text-ink-faint_l dark:text-ink-faint_d text-sm">
          Нет карточек по выбранным фильтрам
        </div>
      ) : (
        <div className="rounded-lg overflow-hidden border border-line-light dark:border-line-dark bg-surface-light dark:bg-surface-dark">
          {rows.map((row, i) => (
            <CardRowItem
              key={row.card.id}
              row={row}
              isLast={i === rows.length - 1}
              onClick={() => onOpenCard(row.card.id)}
            />
          ))}
        </div>
      )}
    </>
  );
}

interface BoardBundle {
  board: BoardRow;
  columns: ColumnRow[];
  lanes: LaneRow[];
  cards: CardRow[];
}

interface Row {
  card: CardRow;
  boardTitle: string;
  columnTitle: string;
  laneTitle: string;
}

// Один useQuery на одну доску. Передаёт результат через коллбэк родителю.
// Обновление родителя — через useEffect, чтобы не нарушать правила React
// (setState во время рендера родителя из дочернего = бесконечный цикл).
function BoardLoader({
  boardId,
  onLoaded,
}: {
  boardId: string;
  onLoaded: (boardId: string, bundle: BoardBundle | null) => void;
}) {
  const q = api.boards.getFull.useQuery({ boardId });
  const data = q.data;
  useEffect(() => {
    if (!data) return;
    onLoaded(boardId, {
      board: data.board as BoardRow,
      columns: data.columns as ColumnRow[],
      lanes: data.lanes as LaneRow[],
      cards: data.cards as CardRow[],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardId, data]);
  return null;
}

function CardRowItem({
  row,
  isLast,
  onClick,
}: {
  row: Row;
  isLast: boolean;
  onClick: () => void;
}) {
  const accent = PRIORITY_COLORS[row.card.priority];
  const dueOverdue = row.card.dueDate ? new Date(row.card.dueDate).getTime() < Date.now() : false;
  const dueClose =
    row.card.dueDate && !dueOverdue
      ? new Date(row.card.dueDate).getTime() - Date.now() < 1000 * 60 * 60 * 24 * 2
      : false;

  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'w-full flex items-center gap-3 px-3 py-2.5 hover:bg-surface2-light dark:hover:bg-surface2-dark text-left transition relative',
        !isLast && 'border-b border-line-light dark:border-line-dark',
      )}
    >
      <span
        className="absolute left-0 top-0 bottom-0 w-[3px]"
        style={{ background: accent }}
      />
      {row.card.asap && (
        <span className="inline-flex items-center justify-center text-[9px] font-bold uppercase tracking-wider px-1 py-px rounded-sm bg-red-500 text-white shrink-0">
          ASAP
        </span>
      )}
      <span className="flex-1 text-sm font-medium text-ink-light dark:text-ink-dark truncate">
        {row.card.title}
      </span>

      <span className="text-[11px] text-ink-faint_l dark:text-ink-faint_d truncate min-w-0 max-w-[140px] hidden sm:inline">
        {row.boardTitle}
        {row.laneTitle && ` · ${row.laneTitle}`}
      </span>
      <span className="text-[11px] text-ink-muted_l dark:text-ink-muted_d truncate min-w-0 max-w-[120px] hidden md:inline">
        {row.columnTitle}
      </span>

      <span
        className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider font-semibold"
        style={{ color: accent, background: `${accent}1f` }}
      >
        {row.card.priority}
      </span>

      {row.card.dueDate && (
        <span
          className={clsx(
            'inline-flex items-center gap-1 text-[11px] tabular-nums shrink-0',
            dueOverdue
              ? 'text-red-500 font-semibold'
              : dueClose
                ? 'text-amber-500'
                : 'text-ink-faint_l dark:text-ink-faint_d',
          )}
        >
          <CalendarDays size={11} strokeWidth={2} />
          {new Date(row.card.dueDate).toLocaleDateString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
          })}
        </span>
      )}

      {row.card.estimate != null && (
        <span className="inline-flex items-center gap-0.5 text-[11px] tabular-nums text-ink-faint_l dark:text-ink-faint_d shrink-0">
          <AlertCircle size={11} strokeWidth={2} />
          {row.card.estimate}sp
        </span>
      )}
    </button>
  );
}

